/**
 * PinSetupModal — 도장 사용 PIN 등록/변경 모달.
 * SealManagementPanel 와 ContractPinGate 양쪽에서 재사용.
 */
import { useState } from "react";
import { createPortal } from "react-dom";
import { usePinStatus, useSetupPin } from "~/services/business/seal";

interface Props {
  /** 닫기 가능 여부 — 게이트로 쓸 때는 false (반드시 등록해야 진입 가능) */
  closable?: boolean;
  onClose?: () => void;
  onSuccess?: () => void;
}

export const PinSetupModal = ({
  closable = true,
  onClose,
  onSuccess,
}: Props) => {
  const { data: pinStatus } = usePinStatus();
  const setupMutation = useSetupPin();
  const hasPin = !!pinStatus?.hasPin;
  const [pin1, setPin1] = useState("");
  const [pin2, setPin2] = useState("");
  const [currentPin, setCurrentPin] = useState("");

  const handle = () => {
    if (!/^\d{6}$/.test(pin1)) return alert("PIN은 숫자 6자리예요");
    if (pin1 !== pin2) return alert("PIN이 서로 달라요");
    if (hasPin && !/^\d{6}$/.test(currentPin)) {
      return alert("기존 PIN 6자리를 입력해 주세요");
    }
    setupMutation.mutate(
      { newPin: pin1, currentPin: hasPin ? currentPin : undefined },
      {
        onSuccess: () => {
          alert(hasPin ? "PIN을 다시 설정했어요" : "PIN이 등록됐어요");
          onSuccess?.();
          onClose?.();
        },
        onError: () => alert("PIN 설정 실패. 기존 PIN 이 맞는지 확인해 주세요"),
      },
    );
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center overflow-y-auto px-4 pt-6 pb-[calc(env(safe-area-inset-bottom)+5.5rem)]"
      onClick={closable ? onClose : undefined}
    >
      <div
        className="bg-white rounded-3xl w-full max-w-md p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-[16px] font-bold mb-1">
          {hasPin ? "PIN 다시 설정" : "사용 PIN 등록"}
        </div>
        <div className="text-[12px] text-[#666] mb-4">
          계약서 발송 시 도장 사용을 인증하는 6자리 숫자예요.
          {!closable && (
            <span className="block mt-1 text-[#F24747] font-semibold">
              계약 관리는 PIN 등록 후 이용할 수 있어요.
            </span>
          )}
        </div>
        {hasPin && (
          <input
            inputMode="numeric"
            maxLength={6}
            value={currentPin}
            onChange={(e) => setCurrentPin(e.target.value.replace(/\D/g, ""))}
            placeholder="기존 PIN 6자리"
            className="w-full px-3 py-2.5 text-[14px] tracking-widest text-center border border-[#E9E9E9] rounded-lg focus:outline-none focus:border-[#FF9934] mb-2"
          />
        )}
        <input
          inputMode="numeric"
          maxLength={6}
          value={pin1}
          onChange={(e) => setPin1(e.target.value.replace(/\D/g, ""))}
          placeholder={hasPin ? "새 PIN 6자리" : "PIN 6자리"}
          className="w-full px-3 py-2.5 text-[14px] tracking-widest text-center border border-[#E9E9E9] rounded-lg focus:outline-none focus:border-[#FF9934] mb-2"
          autoFocus
        />
        <input
          inputMode="numeric"
          maxLength={6}
          value={pin2}
          onChange={(e) => setPin2(e.target.value.replace(/\D/g, ""))}
          placeholder="확인을 위해 한 번 더"
          className="w-full px-3 py-2.5 text-[14px] tracking-widest text-center border border-[#E9E9E9] rounded-lg focus:outline-none focus:border-[#FF9934] mb-3"
        />
        <div className="flex gap-2">
          {closable && (
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2.5 text-[12px] border border-[#E9E9E9] text-[#666] rounded-full"
            >
              취소
            </button>
          )}
          <button
            type="button"
            onClick={handle}
            disabled={setupMutation.isPending}
            className="flex-[2] py-2.5 text-[12px] font-semibold bg-[#FF9934] text-white rounded-full disabled:opacity-50"
          >
            {setupMutation.isPending ? "등록 중…" : "등록하기"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};
